import { FC } from 'react';

import { Program } from '@customTypes/Entities';
import { check, uncheck, checkAll, uncheckAll, getProgramsDate, getProgramsQuery, getSelectedPrograms, setDate, setQuery } from '@features/programsSlice';
import usePrograms from '@hooks/usePrograms';
import { ROUTES } from '@routes/constants';
import { useAppDispatch } from '@store';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';

import ErrorBox from '@components/ErrorBox';
import TranslationStatusBadge from '@components/TranslationStatusBadge';

import EntityMainLayout, { ColumnSortFunction } from '../../layouts/EntityMainLayout';
import NameField from '../../layouts/EntityMainLayout/NameField';
import { sortDates, sortNumbers, sortStrings } from '../../utils/sorters';

const Programs: FC = () => {
  const selected = useSelector(getSelectedPrograms);
  const query = useSelector(getProgramsQuery);
  const date = useSelector(getProgramsDate);

  const dispatch = useAppDispatch();

  const { programs, isFetching: loadingPrograms, error, refetchAll } = usePrograms(date || undefined);

  return (
    <EntityMainLayout<Program>
      error={!!error}
      errorComponent={<ErrorBox message={error ? error.message : ''} clear={() => refetchAll()} />}
      entities={programs}
      loadingEntities={loadingPrograms}
      selected={selected}
      searchProps={{ query, setQuery: (query) => dispatch(setQuery(query)) }}
      dateFilterProps={{ date, setDate: (date) => dispatch(setDate(date)) }}
      columns={[
        {
          key: 'id',
          header: 'ID',
          sort: sortNumbers as ColumnSortFunction,
          style: { maxWidth: '100px', minWidth: '100px' },
        },
        {
          key: 'name',
          header: 'Program name',
          sort: sortStrings as ColumnSortFunction,
          render: (row) => <NameField name={row.name} url={row.url} description={row.description} />,
          style: { minWidth: '450px', width: '100%' },
        },
        {
          key: 'translationStatus',
          header: 'Translation Status',
          sort: sortStrings as ColumnSortFunction,
          style: { width: '200px', minWidth: '200px' },
          render: (row) =>
            row.translationStatus ? (
              <div style={{ margin: 'auto' }}>
                <TranslationStatusBadge status={row.translationStatus} />
              </div>
            ) : (
              ''
            ),
        },
        {
          key: 'channel',
          header: 'Channel',
          style: { width: '200px', minWidth: '200px' },
        },
        {
          key: 'workspace',
          header: 'Workspace',
          style: { width: '200px', minWidth: '200px' },
        },
        {
          key: 'updatedAt',
          header: 'Last modified at',
          style: { width: '200px', minWidth: '200px' },
          sort: sortDates as ColumnSortFunction,
          render: ({ updatedAt }) => DateTime.fromISO(updatedAt.split('Z')[0]).toLocaleString(DateTime.DATETIME_SHORT, { locale: window.navigator.language }),
        },
      ]}
      title={'Marketing Activities'}
      check={(row) => dispatch(check(row))}
      uncheck={(id) => dispatch(uncheck(id))}
      checkAll={(rows) => dispatch(checkAll(rows))}
      uncheckAll={() => dispatch(uncheckAll())}
      newOrderRoute={ROUTES.ORDERS_PROGRAMS}
    />
  );
};

export default Programs;
